import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Music, Brain, Target, Timer, CheckCircle2, XCircle, ArrowRight, Sparkles, Zap, Trophy, RotateCw, UserPlus, Calendar, GraduationCap, Sprout, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GuitarNeck from './GuitarNeck';

import { API_BASE } from "../lib/apiBase";
const API_URL = API_BASE;
const DEMO_TIME = 30;
const FEEDBACK_MS = 500;

// ---- "Test niveau guitare" — hand-picked progressive questions ----
const LEVEL_TEST = [
  {
    question: 'Combien de cordes possède une guitare standard ?',
    options: ['4', '5', '6', '7'],
    correct: '6',
  },
  {
    question: "Quelle gamme majeure n'a aucune altération (# ou ♭) ?",
    options: ['Sol majeur', 'Do majeur', 'Ré majeur', 'Fa majeur'],
    correct: 'Do majeur',
  },
  {
    question: "Combien de dièses contient la gamme de Ré majeur ?",
    options: ['1', '2', '3', '4'],
    correct: '2',
  },
  {
    question: "Quel est l'intervalle entre Do et Sol ?",
    options: ['Tierce', 'Quarte', 'Quinte juste', 'Sixte'],
    correct: 'Quinte juste',
  },
  {
    question: 'De quelles notes est composé un accord de G7 (dominante) ?',
    options: ['Sol — Si — Ré — Fa', 'Sol — Si♭ — Ré — Fa', 'Sol — Si — Ré — Fa#', 'Sol — Do — Mi — Sol'],
    correct: 'Sol — Si — Ré — Fa',
  },
];

const pickN = (arr, n) => [...arr].sort(() => Math.random() - 0.5).slice(0, n);

const buildChordQuestion = (chord, allChords) => {
  const distractors = allChords
    .filter((c) => c.name !== chord.name)
    .sort(() => Math.random() - 0.5)
    .slice(0, 3)
    .map((c) => c.name);
  const options = [...distractors, chord.name].sort(() => Math.random() - 0.5);
  return { chord, options, correct_answer: chord.name };
};

const DEMOS = [
  {
    id: 'level',
    label: 'Test niveau guitare',
    sub: '5 questions pour découvrir votre niveau',
    icon: GraduationCap,
    accent: 'from-[#D4A574]/20 to-[#FAE8C5]',
  },
  {
    id: 'chords',
    label: 'Reconnaissance d\'accords',
    sub: '3 accords à identifier',
    icon: Music,
    accent: 'from-[#D4A574]/20 to-[#EDE8DC]',
  },
  {
    id: 'theory',
    label: 'Quiz théorie express',
    sub: '3 questions en 30 secondes',
    icon: Brain,
    accent: 'from-[#EDE8DC] to-[#FAE8C5]',
  },
];

const levelFromScore = (correctCount) => {
  if (correctCount >= 4) return { label: 'Avancé', color: 'text-[#5D4E37]', Icon: Trophy, msg: "Vous maîtrisez les bases solides de la théorie. Prêt pour le répertoire avancé !" };
  if (correctCount >= 2) return { label: 'Intermédiaire', color: 'text-[#D4A574]', Icon: Star, msg: "De bonnes bases, quelques notions à consolider. Un accompagnement personnalisé débloquera votre progression." };
  return { label: 'Débutant', color: 'text-[#8E7D12]', Icon: Sprout, msg: "Tout est à construire — c'est le meilleur moment pour acquérir les bons réflexes dès le départ." };
};

const HomeDemoGames = () => {
  const { user, loading: authLoading } = useAuth();
  // Demo mode is strictly for public (logged-out) visitors.
  // Any authenticated user (free, premium, admin) bypasses this and uses /mini-jeux directly.
  if (!authLoading && user) return null;

  return <HomeDemoGamesInner />;
};

const HomeDemoGamesInner = () => {
  const [mode, setMode] = useState('picker'); // 'picker' | 'playing' | 'result'
  const [demoId, setDemoId] = useState(null);

  const [questions, setQuestions] = useState([]); // array of normalized {text, options, correct, chord?}
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong'
  const [correct, setCorrect] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DEMO_TIME);
  const [pool, setPool] = useState({ chords: [], theory: [] });
  const timerRef = useRef(null);

  // Preload pool once (cheap — 11 beginner chords + 31 beginner theory)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, tRes] = await Promise.all([
          fetch(`${API_URL}/api/games/chords/beginner`),
          fetch(`${API_URL}/api/games/quiz/beginner/500`),
        ]);
        const cData = await cRes.json();
        const tData = await tRes.json();
        if (!cancelled) {
          setPool({ chords: cData.chords || [], theory: tData.questions || [] });
        }
      } catch (e) {
        console.error('Error preloading demo pools:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Countdown
  useEffect(() => {
    if (mode !== 'playing') return;
    if (timeLeft <= 0) {
      setMode('result');
      return;
    }
    timerRef.current = setTimeout(() => setTimeLeft((v) => v - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timeLeft, mode]);

  const start = useCallback(
    (id) => {
      let qs = [];
      if (id === 'level') {
        qs = LEVEL_TEST.map((q) => ({ type: 'qcm', text: q.question, options: q.options, correct: q.correct }));
      } else if (id === 'chords') {
        if (pool.chords.length < 4) return;
        qs = pickN(pool.chords, 3).map((c) => {
          const q = buildChordQuestion(c, pool.chords);
          return { type: 'chord', text: 'Quel est cet accord ?', options: q.options, correct: q.correct_answer, chord: q.chord };
        });
      } else if (id === 'theory') {
        if (pool.theory.length < 3) return;
        qs = pickN(pool.theory, 3).map((q) => ({
          type: 'qcm',
          text: q.question,
          options: q.options,
          correct: q.correct,
          explanation: q.explanation,
        }));
      }
      setQuestions(qs);
      setIdx(0);
      setSelected(null);
      setFeedback(null);
      setCorrect(0);
      setTimeLeft(DEMO_TIME);
      setDemoId(id);
      setMode('playing');
    },
    [pool]
  );

  const handleAnswer = (opt) => {
    if (feedback || !questions[idx]) return;
    const q = questions[idx];
    const ok = opt === q.correct;
    setSelected(opt);
    setFeedback(ok ? 'correct' : 'wrong');
    if (ok) setCorrect((c) => c + 1);
    setTimeout(() => {
      if (idx + 1 >= questions.length) {
        setMode('result');
      } else {
        setIdx((i) => i + 1);
        setSelected(null);
        setFeedback(null);
      }
    }, FEEDBACK_MS);
  };

  const reset = () => {
    setMode('picker');
    setDemoId(null);
    setQuestions([]);
    setIdx(0);
    setSelected(null);
    setFeedback(null);
    setCorrect(0);
    setTimeLeft(DEMO_TIME);
  };

  // ---- UI ----
  // Picker
  if (mode === 'picker') {
    return (
      <section id="demo-jeux" className="py-20 md:py-24 bg-gradient-to-b from-[#FAFAF7] to-[#F7F3EB]" data-testid="home-demo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10 md:mb-14 space-y-3">
            <p className="text-xs md:text-sm font-light text-[#D4A574] tracking-[0.2em] uppercase flex items-center justify-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Démo gratuite · sans inscription
            </p>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-[#5D4E37]">
              Testez votre oreille musicale
            </h2>
            <p className="text-base font-light text-[#5D4E37]/70 max-w-2xl mx-auto">
              Trois exercices express pour découvrir la plateforme Guitar Savarit — 30 secondes, directement depuis votre navigateur.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {DEMOS.map((d) => {
              const Icon = d.icon;
              const ready = d.id === 'level' || (d.id === 'chords' && pool.chords.length >= 4) || (d.id === 'theory' && pool.theory.length >= 3);
              return (
                <button
                  key={d.id}
                  onClick={() => ready && start(d.id)}
                  disabled={!ready}
                  className="group text-left bg-white border border-[#EDE8DC] hover:border-[#D4A574] rounded-sm p-6 md:p-7 transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid={`demo-card-${d.id}`}
                >
                  <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br ${d.accent} flex items-center justify-center mb-5`}>
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-[#5D4E37]" />
                  </div>
                  <h3 className="text-lg md:text-xl font-serif font-medium text-[#5D4E37] mb-2">
                    {d.label}
                  </h3>
                  <p className="text-sm font-light text-[#5D4E37]/60 mb-5">{d.sub}</p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#D4A574] group-hover:gap-3 transition-all">
                    Jouer <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Playing
  if (mode === 'playing') {
    const q = questions[idx];
    if (!q) return null;
    return (
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#FAFAF7] to-[#F7F3EB]">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          {/* HUD */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={reset}
              className="text-xs md:text-sm text-[#5D4E37]/60 hover:text-[#5D4E37] transition-colors"
              data-testid="demo-exit-btn"
            >
              ← Retour
            </button>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-[#5D4E37]/70 tabular-nums" data-testid="demo-progress">
                {idx + 1}/{questions.length}
              </span>
              <span className={`flex items-center gap-1 font-semibold tabular-nums ${timeLeft <= 5 ? 'text-red-500' : 'text-[#5D4E37]'}`} data-testid="demo-time">
                <Timer className="w-4 h-4" /> {timeLeft}s
              </span>
            </div>
          </div>

          <Card className="border-2 border-[#D4A574]">
            <CardContent className="p-5 md:p-7 space-y-5">
              {q.type === 'chord' && q.chord && (
                <div className="bg-[#EDE8DC]/20 p-3 md:p-4 rounded-sm">
                  <GuitarNeck
                    positions={q.chord.positions}
                    fingers={q.chord.fingers}
                    muted={q.chord.muted}
                  />
                </div>
              )}

              <p className="text-base md:text-xl font-serif text-[#5D4E37] leading-snug" data-testid="demo-question">
                {q.text}
              </p>

              <div className={`grid gap-2.5 ${q.options.length === 2 ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2'}`}>
                {q.options.map((opt, i) => {
                  const isCorrect = opt === q.correct;
                  const isSelected = selected === opt;
                  let cls = 'bg-[#EDE8DC] hover:bg-[#D4A574] hover:text-white text-[#5D4E37] transition-colors';
                  if (feedback) {
                    if (isCorrect) cls = 'bg-green-500 text-white';
                    else if (isSelected) cls = 'bg-red-500 text-white';
                    else cls = 'bg-[#EDE8DC] text-[#5D4E37] opacity-60';
                  }
                  return (
                    <Button
                      key={i}
                      onClick={() => handleAnswer(opt)}
                      disabled={!!feedback}
                      className={`h-auto min-h-12 md:min-h-14 py-3 px-4 text-sm md:text-base whitespace-normal ${cls}`}
                      data-testid={`demo-option-${i}`}
                    >
                      {opt}
                    </Button>
                  );
                })}
              </div>

              {feedback && q.explanation && (
                <div className="p-3 rounded-sm border bg-[#FAF4E8] border-[#D4A574]/40 text-xs md:text-sm text-[#5D4E37]/80 flex items-start gap-2">
                  {feedback === 'correct' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <span>{q.explanation}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // Result
  const total = questions.length;
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
  const isLevelTest = demoId === 'level';
  const classification = isLevelTest ? levelFromScore(correct) : null;

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-[#FAFAF7] to-[#F7F3EB]">
      <div className="max-w-2xl mx-auto px-4 md:px-6" data-testid="demo-result-screen">
        <Card className="border-2 border-[#D4A574] overflow-hidden">
          <div className="bg-gradient-to-br from-[#D4A574]/15 via-[#FAE8C5] to-white px-6 md:px-8 py-8 md:py-10 text-center space-y-3">
            <Trophy className="w-10 h-10 md:w-12 md:h-12 text-[#D4A574] mx-auto" />
            {isLevelTest ? (
              <>
                <p className="text-xs font-light text-[#D4A574] tracking-widest uppercase">Votre niveau</p>
                <p className={`text-4xl md:text-5xl font-serif font-semibold ${classification.color} inline-flex items-center justify-center gap-3`} data-testid="demo-level-result">
                  <classification.Icon className="w-9 h-9 md:w-10 md:h-10" />
                  {classification.label}
                </p>
                <p className="text-sm md:text-base font-light text-[#5D4E37]/80 max-w-md mx-auto pt-2">
                  {classification.msg}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs font-light text-[#D4A574] tracking-widest uppercase">Résultat</p>
                <p className="text-4xl md:text-5xl font-serif font-semibold text-[#5D4E37]" data-testid="demo-result-score">
                  {correct}/{total}
                </p>
                <p className="text-sm text-[#5D4E37]/60">{accuracy}% de précision</p>
              </>
            )}
          </div>

          <CardContent className="p-6 md:p-8 space-y-5">
            <div className="text-center space-y-2">
              <p className="text-sm md:text-base font-semibold text-[#5D4E37] flex items-center justify-center gap-2">
                <Zap className="w-4 h-4 text-[#D4A574]" />
                Prêt à aller plus loin ?
              </p>
              <p className="text-xs md:text-sm font-light text-[#5D4E37]/70 max-w-md mx-auto">
                La plateforme complète inclut un LMS gamifié (60 accords, 95 questions de théorie, chrono arcade, records) et des leçons privées à Genève.
              </p>
            </div>

            <div className="space-y-2.5">
              <Link to="/register" className="block">
                <Button className="w-full bg-[#5D4E37] hover:bg-[#6B5D4F] text-white h-11 md:h-12 flex items-center justify-center gap-2" data-testid="demo-cta-register">
                  <UserPlus className="w-4 h-4" />
                  Créer un compte gratuit
                </Button>
              </Link>
              <Link to="/booking" className="block">
                <Button variant="outline" className="w-full border-[#5D4E37] text-[#5D4E37] hover:bg-[#5D4E37] hover:text-white h-11 md:h-12 flex items-center justify-center gap-2" data-testid="demo-cta-booking">
                  <Calendar className="w-4 h-4" />
                  Réserver ma 1<sup>ère</sup> leçon
                </Button>
              </Link>
              <Link to="/mini-jeux" className="block">
                <Button variant="ghost" className="w-full text-[#D4A574] hover:bg-[#FAF4E8] h-11 md:h-12 flex items-center justify-center gap-2" data-testid="demo-cta-platform">
                  <GraduationCap className="w-4 h-4" />
                  Débloquer la plateforme élève
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-4 pt-3 border-t border-[#EDE8DC]">
              <button
                onClick={() => start(demoId)}
                className="text-xs md:text-sm text-[#5D4E37]/60 hover:text-[#5D4E37] transition-colors flex items-center gap-1"
                data-testid="demo-replay-btn"
              >
                <RotateCw className="w-3.5 h-3.5" /> Rejouer cette démo
              </button>
              <span className="text-[#5D4E37]/20">·</span>
              <button
                onClick={reset}
                className="text-xs md:text-sm text-[#5D4E37]/60 hover:text-[#5D4E37] transition-colors"
                data-testid="demo-other-btn"
              >
                Tester un autre jeu
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default HomeDemoGames;
